import React from "react";
import { Link } from "react-router-dom";

// DEEP-CLEAN-1 — chunk-load recovery. When a deploy ships new hashed JS/CSS chunks, a browser tab that
// still holds the OLD index.html knows only the old chunk filenames. The first lazy import() to a
// rebuilt route then fetches a hash that no longer exists on the server and throws a ChunkLoadError at
// mount — which this boundary previously rendered as the worst-moment error card (the UPLOAD-FIX-1
// report: a full-page crash on exactly the page the last deploy changed, unreproducible in jsdom because
// jsdom never fetches real chunks). The cure is a fresh document: reload ONCE to pull the new index.html
// (and its new chunk manifest), then the route loads. A time-boxed sessionStorage stamp makes the reload
// self-limiting — a genuinely-missing chunk that fails again immediately can never loop the tab; after
// the window elapses a later, unrelated stale-deploy is allowed its own single reload.
export const CHUNK_RELOAD_KEY = "cognivio:chunkReloadAt";
export const CHUNK_RELOAD_WINDOW_MS = 10_000;

export function isChunkLoadError(error) {
  if (!error) return false;
  const name = String(error.name || "");
  const code = String(error.code || "");
  const message = String((error && error.message) || error || "");
  return (
    name === "ChunkLoadError" ||
    code === "CSS_CHUNK_LOAD_FAILED" ||
    /Loading chunk [\w./-]+ failed/i.test(message) ||
    /Loading CSS chunk [\w./-]+ failed/i.test(message) ||
    /failed to (fetch|import)[^]*?(dynamically )?imported module/i.test(message) ||
    /importing (?:a )?module script failed/i.test(message)
  );
}

function reloadedRecently() {
  try {
    const raw = window.sessionStorage.getItem(CHUNK_RELOAD_KEY);
    if (!raw) return false;
    const ts = Number(raw);
    return Number.isFinite(ts) && ts > 0 && Date.now() - ts < CHUNK_RELOAD_WINDOW_MS;
  } catch {
    // A blocked sessionStorage (private mode, disabled cookies) means we cannot dedupe a reload, so we
    // treat it as "already reloaded" — the honest error is strictly safer than an unguarded reload loop.
    return true;
  }
}

function markReloaded() {
  try {
    window.sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
  } catch {
    /* storage unavailable — reloadedRecently() already fails closed */
  }
}

export class RouteErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, reloading: false };
    this.handleRetry = this.handleRetry.bind(this);
  }

  static getDerivedStateFromError(error) {
    // A first stale-chunk failure renders nothing (a fresh document is imminent); the second — the flag
    // is already stamped — falls through to the honest surface. A non-chunk throw always shows the card.
    const reloading = isChunkLoadError(error) && !reloadedRecently();
    return { hasError: true, error, reloading };
  }

  componentDidCatch(error, info) {
    if (isChunkLoadError(error)) {
      if (!reloadedRecently()) {
        markReloaded();
        // eslint-disable-next-line no-console
        console.warn("Route chunk failed to load (stale build) — reloading once for a fresh document.");
        try {
          window.location.reload();
        } catch {
          /* jsdom / no-location: the honest surface still renders on the next getDerivedStateFromError */
        }
      } else {
        // eslint-disable-next-line no-console
        console.error("Route chunk still failing after a reload — showing the honest error.", error?.message || error);
      }
      return;
    }

    // UX-2 Step 5(b): capture field throws (like F-2) so they are diagnosable, in ALL envs — the
    // message + stack + component stack reach the console (the house logging path; no reporter
    // dependency). Previously this only logged in development, so production throws were silent.
    // eslint-disable-next-line no-console
    console.error("Route failed to render", error?.message || error, error?.stack, info?.componentStack);
  }

  handleRetry() {
    // For a stale chunk, a soft re-render just re-hits webpack's cached rejected import — the real cure is
    // a fresh document, so clear the guard and hard-reload. A field throw keeps the soft reset (the
    // transient data condition may have cleared).
    if (isChunkLoadError(this.state.error)) {
      try {
        window.sessionStorage.removeItem(CHUNK_RELOAD_KEY);
      } catch {
        /* ignore */
      }
      try {
        window.location.reload();
        return;
      } catch {
        /* fall through to a soft reset */
      }
    }
    this.setState({ hasError: false, error: null, reloading: false });
  }

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    if (this.state.reloading) {
      // A fresh document is loading — never flash the worst-moment card during the self-heal.
      return null;
    }

    return (
      <div className="flex min-h-screen items-center justify-center bg-ivory px-4">
        {/* UX-5: brand-graceful worst-moment surface — ivory field, warm paper card, serif headline,
            one gold rule. */}
        <div className="max-w-md rounded-xl border border-slate-200 bg-paper p-6 text-center shadow-sm">
          <h1 className="font-display text-xl font-semibold text-ink">Something went wrong loading this page.</h1>
          <span className="mx-auto mt-3 block h-1 w-12 rounded-full bg-gold" aria-hidden="true" />
          <p className="mt-3 text-sm leading-6 text-slate-600">
            Try again, or return to your dashboard and reopen the page from there.
          </p>
          {process.env.NODE_ENV === "development" && this.state.error ? (
            <pre className="mt-3 overflow-auto rounded-md bg-slate-100 p-3 text-left text-[11px] text-slate-700">
              {this.state.error.message || String(this.state.error)}
            </pre>
          ) : null}
          <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:justify-center">
            <button
              type="button"
              onClick={this.handleRetry}
              className="inline-flex min-h-[44px] items-center justify-center rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100"
            >
              Try again
            </button>
            <Link
              to="/"
              className="inline-flex min-h-[44px] items-center justify-center rounded-md bg-ink px-4 py-2 text-sm font-semibold text-white hover:bg-ink-2"
            >
              Go to my dashboard
            </Link>
          </div>
        </div>
      </div>
    );
  }
}

export default RouteErrorBoundary;
